import { useMemo } from 'react';
import { Badge, DataTable, EmptyState, FormCheckbox, Tooltip } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { PortfolioStrategyExpectation, PortfolioStrategyExpectationStats } from '../performanceTypes';
import {
    formatDays,
    formatRatePercent,
    formatRatio,
    formatSignedCurrency,
    formatSignedPercent,
    signedToneClass,
    thresholdToneClass
} from '../performanceFormat';
import {
    describeStrategyActivity,
    formatShortDate,
    isDormantStrategyRow,
    sortStrategyRows,
    STRATEGY_STATUS_LABELS,
    type StrategyAttributionRow,
    type StrategyStatus
} from '../performanceUtils';
import StrategyColorDot from './StrategyColorDot';

interface StrategyAttributionTableProps {
    rows: StrategyAttributionRow[];
    strategyColors: Map<number, string>;
    selectedStrategyIds: number[];
    onToggleStrategy: (strategyId: number) => void;
    /** Show subscribed strategies that have never traded. */
    showDormant: boolean;
    onShowDormantChange: (show: boolean) => void;
    compareToBacktest: boolean;
    onCompareToBacktestChange: (enabled: boolean) => void;
    expectations: Map<number, PortfolioStrategyExpectation> | null;
    expectationsLoading: boolean;
}

type ExpectationWindow = 'walkForward' | 'training' | 'overall';

interface ExpectedValues {
    window: ExpectationWindow;
    stats: PortfolioStrategyExpectationStats['walkForward'];
    expectation: PortfolioStrategyExpectation;
}

const STATUS_BADGE_VARIANTS: Record<StrategyStatus, 'success' | 'warning' | 'dark'> = {
    active: 'success',
    paused: 'warning',
    unsubscribed: 'dark'
};

/** Walk-forward results are the closest analogue to live trading; fall back to in-sample, then all entries. */
const pickExpectedValues = (expectation: PortfolioStrategyExpectation | undefined): ExpectedValues | null => {
    if (!expectation?.stats) return null;

    if (expectation.stats.walkForward.acceptedTradeCount > 0)
        return { window: 'walkForward', stats: expectation.stats.walkForward, expectation };
    if (expectation.stats.training.acceptedTradeCount > 0) return { window: 'training', stats: expectation.stats.training, expectation };
    if (expectation.stats.acceptedTradeCount > 0) return { window: 'overall', stats: expectation.stats, expectation };

    return null;
};

const WINDOW_LABELS: Record<ExpectationWindow, string> = {
    walkForward: 'walk-forward',
    training: 'in-sample',
    overall: 'all entries'
};

const Sparkline = ({ values, color }: { values: number[]; color: string }) => {
    if (values.length < 2) return <span className="text-zinc-600">—</span>;

    const width = 84;
    const height = 22;
    const low = Math.min(...values, 0);
    const high = Math.max(...values, 0);
    const span = high - low || 1;
    const x = (index: number) => 1 + ((width - 2) * index) / (values.length - 1);
    const y = (value: number) => height - 2 - ((height - 4) * (value - low)) / span;
    const path = values.map((value, index) => `${index ? 'L' : 'M'}${x(index).toFixed(1)} ${y(value).toFixed(1)}`).join(' ');

    return (
        <svg aria-hidden="true" height={height} viewBox={`0 0 ${width} ${height}`} width={width}>
            <line stroke="#3f3f46" strokeDasharray="2 2" x1="1" x2={width - 1} y1={y(0)} y2={y(0)} />
            <path d={path} fill="none" stroke={color} strokeWidth="1.5" />
        </svg>
    );
};

const ContributionBar = ({ value, maxAbs }: { value: number; maxAbs: number }) => {
    const width = maxAbs > 0 ? Math.max(2, (Math.abs(value) / maxAbs) * 56) : 0;

    return (
        <span className="inline-flex w-[120px] items-center">
            <span className="flex w-[58px] justify-end">
                {value < 0 && <span className="h-2.5 rounded-sm bg-red-500" style={{ width }} />}
            </span>
            <span className="flex w-[58px] justify-start border-l border-zinc-700">
                {value > 0 && <span className="h-2.5 rounded-sm bg-[#29a740]" style={{ width }} />}
            </span>
        </span>
    );
};

const DeltaCell = ({
    live,
    expected,
    format,
    threshold
}: {
    live: number | null;
    expected: number | null;
    format: (value: number | null) => string;
    threshold?: number;
}) => {
    if (expected === null) return <span className="text-zinc-600">—</span>;
    const delta = live === null ? null : Math.round((live - expected) * 100) / 100;

    return (
        <span className="whitespace-nowrap">
            <span className={threshold === undefined ? 'text-zinc-300' : thresholdToneClass(expected, threshold, 'text-zinc-300')}>
                {format(expected)}
            </span>
            {delta !== null && (
                <span className={`ml-1.5 text-xs ${signedToneClass(delta)}`}>
                    ({delta > 0 ? '+' : ''}
                    {format(delta).replace('+', '')})
                </span>
            )}
        </span>
    );
};

const StrategyAttributionTable = ({
    rows,
    strategyColors,
    selectedStrategyIds,
    onToggleStrategy,
    showDormant,
    onShowDormantChange,
    compareToBacktest,
    onCompareToBacktestChange,
    expectations,
    expectationsLoading
}: StrategyAttributionTableProps) => {
    const sortedRows = useMemo(() => sortStrategyRows(rows), [rows]);
    const dormantCount = useMemo(() => sortedRows.filter(isDormantStrategyRow).length, [sortedRows]);
    const visibleRows = useMemo(
        () => (showDormant ? sortedRows : sortedRows.filter((row) => !isDormantStrategyRow(row))),
        [sortedRows, showDormant]
    );
    const selectedSet = useMemo(() => new Set(selectedStrategyIds), [selectedStrategyIds]);
    const maxAbsPeriodPnl = useMemo(() => Math.max(0, ...visibleRows.map((row) => Math.abs(row.periodPnl))), [visibleRows]);

    const columns = useMemo<ColumnDef<StrategyAttributionRow>[]>(() => {
        const base: ColumnDef<StrategyAttributionRow>[] = [
            {
                accessorKey: 'name',
                header: 'Strategy',
                minSize: 240,
                size: 320,
                maxSize: 440,
                cell: ({ row }) => {
                    const entry = row.original;

                    return (
                        <div className="min-w-0" title={`${entry.name} (#${entry.strategyId})`}>
                            <div className="flex min-w-0 items-start gap-2">
                                <StrategyColorDot className="mt-1.5" color={strategyColors.get(entry.strategyId) ?? '#71717a'} />
                                <span className="line-clamp-2 min-w-0 whitespace-normal break-words font-medium text-white">
                                    {entry.name}
                                </span>
                                <span className="flex shrink-0 items-center gap-1.5 pt-0.5">
                                    <span className="text-xs text-zinc-500">#{entry.strategyId}</span>
                                    <Badge className="whitespace-nowrap" variant={STATUS_BADGE_VARIANTS[entry.status]}>
                                        {STRATEGY_STATUS_LABELS[entry.status]}
                                    </Badge>
                                    {selectedSet.has(entry.strategyId) && (
                                        <Badge className="whitespace-nowrap" variant="info">
                                            In scope
                                        </Badge>
                                    )}
                                </span>
                            </div>
                            <div className="mt-0.5 truncate pl-4 text-xs text-zinc-500">{describeStrategyActivity(entry)}</div>
                        </div>
                    );
                }
            },
            { accessorKey: 'closedCount', header: 'Closed', minSize: 70, size: 80, maxSize: 100 },
            { accessorKey: 'openCount', header: 'Open', minSize: 60, size: 70, maxSize: 90 },
            {
                accessorKey: 'winRate',
                header: 'Win rate',
                minSize: 80,
                size: 90,
                maxSize: 110,
                cell: ({ row }) => (
                    <span className={thresholdToneClass(row.original.winRate, 50)}>{formatRatePercent(row.original.winRate, 0)}</span>
                )
            },
            {
                accessorKey: 'realizedPnl',
                header: 'Realized',
                minSize: 100,
                size: 120,
                maxSize: 150,
                cell: ({ row }) => (
                    <span className={signedToneClass(row.original.realizedPnl)}>{formatSignedCurrency(row.original.realizedPnl)}</span>
                )
            },
            {
                accessorKey: 'openPnl',
                header: 'Open P&L',
                minSize: 90,
                size: 110,
                maxSize: 140,
                cell: ({ row }) => (
                    <span className={signedToneClass(row.original.openPnl)}>{formatSignedCurrency(row.original.openPnl, 0)}</span>
                )
            },
            {
                accessorKey: 'totalPnl',
                header: 'Total',
                minSize: 90,
                size: 110,
                maxSize: 140,
                cell: ({ row }) => (
                    <span className={`font-semibold ${signedToneClass(row.original.totalPnl)}`}>
                        {formatSignedCurrency(row.original.totalPnl, 0)}
                    </span>
                )
            },
            {
                accessorKey: 'periodPnl',
                header: () => (
                    <span title="Realized closes plus mark-to-market changes attributed to the strategy across the selected period">
                        Period P&L
                    </span>
                ),
                minSize: 180,
                size: 220,
                maxSize: 260,
                cell: ({ row }) => (
                    <span className="inline-flex items-center gap-2">
                        <ContributionBar maxAbs={maxAbsPeriodPnl} value={row.original.periodPnl} />
                        <span className={signedToneClass(row.original.periodPnl)}>{formatSignedCurrency(row.original.periodPnl, 0)}</span>
                    </span>
                )
            },
            {
                accessorKey: 'profitFactor',
                header: 'Profit factor',
                minSize: 90,
                size: 100,
                maxSize: 120,
                cell: ({ row }) => (
                    <span className={thresholdToneClass(row.original.profitFactor, 1)}>{formatRatio(row.original.profitFactor)}</span>
                )
            },
            {
                accessorKey: 'averageProfitPercentage',
                header: 'Avg trade',
                minSize: 80,
                size: 95,
                maxSize: 120,
                cell: ({ row }) => (
                    <span className={signedToneClass(row.original.averageProfitPercentage)}>
                        {formatSignedPercent(row.original.averageProfitPercentage, 1)}
                    </span>
                )
            },
            {
                accessorKey: 'averageHoldDays',
                header: 'Avg hold',
                minSize: 75,
                size: 85,
                maxSize: 110,
                cell: ({ row }) => formatDays(row.original.averageHoldDays)
            },
            {
                accessorKey: 'lastActivityDate',
                header: 'Last activity',
                minSize: 95,
                size: 110,
                maxSize: 130,
                cell: ({ row }) => (row.original.lastActivityDate ? formatShortDate(row.original.lastActivityDate) : '—')
            },
            {
                id: 'trend',
                header: 'Period trend',
                minSize: 95,
                size: 100,
                maxSize: 120,
                enableSorting: false,
                cell: ({ row }) => (
                    <Sparkline color={strategyColors.get(row.original.strategyId) ?? '#71717a'} values={row.original.cumulativePeriodPnl} />
                )
            }
        ];

        if (!compareToBacktest) return base;

        const expectedFor = (strategyId: number) => pickExpectedValues(expectations?.get(strategyId));

        const expectedColumns: ColumnDef<StrategyAttributionRow>[] = [
            {
                id: 'expectedSource',
                header: 'Baseline',
                minSize: 140,
                size: 170,
                maxSize: 220,
                enableSorting: false,
                cell: ({ row }) => {
                    const expectation = expectations?.get(row.original.strategyId);
                    if (expectationsLoading && !expectation) return <span className="text-zinc-500">Loading…</span>;
                    if (!expectation) return <span className="text-zinc-600">No winning baseline linked</span>;

                    const values = pickExpectedValues(expectation);

                    return (
                        <Tooltip
                            content={
                                expectation.backtest
                                    ? `${expectation.backtest.name} (#${expectation.backtest.id}) · ${
                                          expectation.source === 'refresh' ? 'latest auto-refresh of the baseline' : 'winning baseline'
                                      } · ${expectation.backtest.startDateTime} → ${expectation.backtest.endDateTime}${
                                          values
                                              ? ` · ${WINDOW_LABELS[values.window]} window, ${values.stats.acceptedTradeCount} accepted trades`
                                              : ''
                                      }${expectation.issue ? ` · ${expectation.issue}` : ''}`
                                    : (expectation.issue ?? 'Unavailable')
                            }
                        >
                            <span
                                className={`cursor-help border-b border-dotted border-zinc-600 ${values ? 'text-zinc-300' : 'text-amber-300'}`}
                            >
                                {values ? `#${expectation.backtestId} · ${WINDOW_LABELS[values.window]}` : 'No results'}
                            </span>
                        </Tooltip>
                    );
                }
            },
            {
                id: 'expectedWinRate',
                header: () => (
                    <span title="Expected win rate from the baseline backtest, with the live difference in parentheses">BT win rate</span>
                ),
                minSize: 110,
                size: 130,
                maxSize: 160,
                enableSorting: false,
                cell: ({ row }) => (
                    <DeltaCell
                        expected={expectedFor(row.original.strategyId)?.stats.winRatePercent ?? null}
                        format={(value) => formatRatePercent(value, 0)}
                        live={row.original.winRate}
                        threshold={50}
                    />
                )
            },
            {
                id: 'expectedAvgTrade',
                header: () => (
                    <span title="Expected average trade return from the baseline backtest, with the live difference in parentheses">
                        BT avg trade
                    </span>
                ),
                minSize: 110,
                size: 130,
                maxSize: 160,
                enableSorting: false,
                cell: ({ row }) => (
                    <DeltaCell
                        expected={expectedFor(row.original.strategyId)?.stats.averageTradeProfitPercent ?? null}
                        format={(value) => formatSignedPercent(value, 1)}
                        live={row.original.averageProfitPercentage}
                    />
                )
            },
            {
                id: 'expectedProfitFactor',
                header: () => (
                    <span title="Expected profit factor from the baseline backtest, with the live difference in parentheses">
                        BT profit factor
                    </span>
                ),
                minSize: 110,
                size: 130,
                maxSize: 160,
                enableSorting: false,
                cell: ({ row }) => (
                    <DeltaCell
                        expected={expectedFor(row.original.strategyId)?.stats.profitFactor ?? null}
                        format={(value) => formatRatio(value)}
                        live={row.original.profitFactor === Infinity ? null : row.original.profitFactor}
                        threshold={1}
                    />
                )
            }
        ];

        return [
            ...base.slice(0, 4),
            ...expectedColumns.slice(1, 2),
            ...base.slice(4, 10),
            ...expectedColumns.slice(2),
            ...base.slice(10),
            expectedColumns[0]
        ];
    }, [compareToBacktest, expectations, expectationsLoading, maxAbsPeriodPnl, selectedSet, strategyColors]);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-zinc-400">
                <span>
                    Rows are grouped by activity in the period. Click a row to add it to the page's strategy scope; click again to remove
                    it.
                </span>
                <div className="flex flex-wrap items-center gap-4">
                    {dormantCount > 0 && (
                        <FormCheckbox
                            checked={showDormant}
                            label={`Show ${dormantCount} subscribed ${dormantCount === 1 ? 'strategy' : 'strategies'} with no trades`}
                            onChange={(event) => onShowDormantChange(event.target.checked)}
                        />
                    )}
                    <FormCheckbox
                        checked={compareToBacktest}
                        label="Compare to winning baseline backtest"
                        onChange={(event) => onCompareToBacktestChange(event.target.checked)}
                    />
                </div>
            </div>
            {visibleRows.length ? (
                <DataTable
                    search={false}
                    sort
                    striped
                    columns={columns}
                    data={visibleRows}
                    getRowId={(row) => String(row.strategyId)}
                    onRowClick={(row) => onToggleStrategy(row.strategyId)}
                />
            ) : (
                <EmptyState
                    description={
                        dormantCount > 0
                            ? `${dormantCount} subscribed ${dormantCount === 1 ? 'strategy has' : 'strategies have'} no trades yet.`
                            : undefined
                    }
                    size="sm"
                    title="No strategy traded or held positions in this period"
                />
            )}
            {compareToBacktest && (
                <div className="text-xs text-zinc-500">
                    Expected values come from each strategy's winning-baseline portfolio backtest (its latest auto-refresh when enabled),
                    using the walk-forward window when it has trades. Parentheses show live minus expected.
                </div>
            )}
        </div>
    );
};

export default StrategyAttributionTable;
